import { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { RepairItem, RepairStatus } from "types/restarter";
import { REPAIR_STATUSES } from "libraries/restarter";
import { useRestarterItemEdit } from "hooks/useRestarterItemEdit";

type Props = {
  item: RepairItem;
};

const RepairItemWidget = ({ item }: Props) => {
  const [open, setOpen] = useState(false);
  const [editedItem, setEditedItem] = useState<RepairItem>(item);
  const { submitRestarterItem, loading } = useRestarterItemEdit();

  const itemStatus = (item.status || "IN PROGRESS") as RepairStatus;
  const status = REPAIR_STATUSES[itemStatus];

  const handleClickOpen = () => {
    setEditedItem(item);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleStatusChange = (event: SelectChangeEvent<RepairStatus>) => {
    const newStatus = event.target.value as RepairStatus;
    setEditedItem({ ...editedItem, status: newStatus });
  };

  const handleSubmit = async () => {
    const success = await submitRestarterItem(editedItem);
    if (success) {
      // Ideally, we would refetch the data here or update the parent state
      // For now, we can update the item in the component state to reflect the change
      item.status = editedItem.status;
    }
    handleClose();
  };

  return (
    <>
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h6" component="div" gutterBottom>
              {item.item}
            </Typography>
            {item.id && <Button onClick={handleClickOpen}>Edit</Button>}
          </Box>
          {item.category && (
            <Chip
              label={`${item.category.emoji} ${item.category.label}`}
              size="small"
              sx={{ mb: 1 }}
            />
          )}
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Brand:
              </Typography>
              <Typography variant="body1">{item.brand}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Model:
              </Typography>
              <Typography variant="body1">{item.model}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Age:
              </Typography>
              <Typography variant="body1">{item.age} years</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Weight:
              </Typography>
              <Typography variant="body1">{item.weight} kg</Typography>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Assessment:
            </Typography>
            <Typography variant="body1">{item.assessment}</Typography>
          </Box>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Price when new:
              </Typography>
              <Typography fontWeight="500" variant="body1">
                {item.priceAsNew} KR
              </Typography>
            </Box>
            {status && (
              <Chip
                label={`${status.emoji} ${status.label}`}
                color={status.color as any}
              />
            )}
          </Box>
        </CardContent>
      </Card>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>Edit Repair Item Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              value={editedItem.status || "IN PROGRESS"}
              label="Status"
              onChange={handleStatusChange}
            >
              {Object.keys(REPAIR_STATUSES).map((key) => (
                <MenuItem key={key} value={key}>
                  {REPAIR_STATUSES[key as RepairStatus].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RepairItemWidget;
